import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, statusClass, statusLabel } from '../lib/format.ts';

interface InvoiceLine {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  qty: number;
  unitPrice: number;
  discountRate: number;
  subTotal: number;
  location: string | null;
}

interface InvoiceDetail {
  id: number;
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string | null;
  customerNo: string | null;
  customerName: string | null;
  salesman: string | null;
  currency: string;
  value: number;
  outstanding: number | null;
  status: string;
  ref: string | null;
  lines: InvoiceLine[];
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () =>
      apiFetch<InvoiceDetail>(`/api/dashboard/invoices/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  // Show only real product lines (with a SKU); OBM invoices also carry blank
  // rows and free-text note lines (PROJECT / address) that just add noise here.
  const productLines = (data?.lines ?? []).filter((l) => l.productCode);

  return (
    <>
      <Link to="/invoices" className="back-link">← All invoices</Link>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">Invoice {data.invoiceNo}</h1>
              <span className="main-subtitle mono">{dateOnly(data.invoiceDate)}</span>
            </div>
            <span className={`badge ${statusClass(data.status)}`}>
              {statusLabel(data.status)}
            </span>
          </header>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Header</h2>
            </header>
            <dl className="detail-grid">
              <dt>Invoice no</dt>
              <dd className="mono">{data.invoiceNo}</dd>
              <dt>Invoice date</dt>
              <dd>{dateOnly(data.invoiceDate)}</dd>
              <dt>Due date</dt>
              <dd>{dateOnly(data.dueDate)}</dd>
              <dt>Customer</dt>
              <dd>
                {data.customerNo ? (
                  <Link to={`/customers/${encodeURIComponent(data.customerNo)}`}>
                    {data.customerName ?? data.customerNo}{' '}
                    <span className="mono" style={{ color: 'var(--text-dim)' }}>
                      ({data.customerNo})
                    </span>
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Salesman</dt>
              <dd>{data.salesman || '—'}</dd>
              <dt>Reference</dt>
              <dd className="mono">{data.ref || '—'}</dd>
              <dt>Value</dt>
              <dd>{money(data.value, data.currency)}</dd>
              <dt>Outstanding</dt>
              <dd>{money(data.outstanding, data.currency)}</dd>
            </dl>
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Line items</h2>
              <span className="panel-subtitle">
                {productLines.length} item{productLines.length === 1 ? '' : 's'}
              </span>
            </header>
            {productLines.length === 0 ? (
              <div className="empty">
                No priced SKU lines on file. Run <code>--mode L</code> on the agent to fetch{' '}
                <code>items_data</code> from OBM.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>SKU</th>
                    <th>Description</th>
                    <th>Loc</th>
                    <th className="num">Qty</th>
                    <th className="num">Unit price</th>
                    <th className="num">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {productLines.map((l) => (
                    <tr key={l.lineNo}>
                      <td className="mono">{l.lineNo}</td>
                      <td className="mono">
                        {l.productCode ? (
                          <Link to={`/skus/${encodeURIComponent(l.productCode)}`}>
                            {l.productCode}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{l.description || '—'}</td>
                      <td className="mono">{l.location || '—'}</td>
                      <td className="num">{l.qty}</td>
                      <td className="num">{money(l.unitPrice, data.currency)}</td>
                      <td className="num">{money(l.subTotal, data.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}
